import { MAX_COUNT, levelForXp, unlockedSpecies, SAVE_KEY, SAVE_KEYS, createSave, exportSave, importSave, type ImportResult, type SaveOutcome, type SaveStorage } from '../sim/index';
import { openVoyageSession, type SailResult, type VoyageOrder, type VoyageSession, type VoyageView, withVoyageOrder } from '../voyage/index';
import { profileStorage, withPlayerProfile } from '../profile/persistence';
import { createPlayerProfile, normalisePlayerProfile, profileFromEnvelope, type PlayerProfile, type PlayerSettings, type TutorialMode } from '../profile/schema';
import { destinationResult, reconcileProgress } from './goals';
import type { CatchSnapshot } from './catch-presentation';
import { buyOutfitterItem, equipOutfitterItem, normaliseFisherIdentity, outfitterView, validateFisherIdentity, type CosmeticSlot, type FisherIdentity, type OutfitterView, type TransactionResult } from './outfitter';

import { acceptHarbourCommission, buyHarbourEquipment, claimHarbourCommission, fundHarbourProject, harbourIdentity, harbourOutfitterFunds, harbourView, harbourWallet, observeHarbour, rewardHarbourChallenge, type HarbourChange, type HarbourContext, type HarbourState, type HarbourView } from './harbour';
import { validateHarbourGameProof, type HarbourGameProof } from './harbour-games';

export type BackupSource = 'import' | 'new-voyage' | 'manual';
export interface RecoverySummary { readonly available: boolean; readonly source: BackupSource | null; readonly timestamp: number | null; readonly note: string; readonly salvageAvailable?: boolean; }
export interface RecoveryStore { inspect(): RecoverySummary; backup(source: BackupSource, nowMs: number): SaveOutcome; restore(nowMs: number): ImportResult; clearBackup(): SaveOutcome; exportRaw(): string | null; }
export interface SaveProblem { readonly kind: 'invalid' | 'unreadable'; readonly reason: string; readonly rawAvailable: boolean; }
export type StartResult = { readonly ok: true } | { readonly ok: false; readonly reason: string };
export interface PlayerSession {
  voyage(): VoyageSession;
  profile(): PlayerProfile;
  updateProfile(update: (profile: PlayerProfile) => PlayerProfile): void;
  updateSettings(patch: Partial<PlayerSettings>): void;
  setTutorial(mode: TutorialMode): void;
  /** Explicit gameplay commands; navigation input stays in AppAction. */
  tick(nowMs: number): VoyageView;
  setTarget(speciesId: string, nowMs: number): void;
  sailTo(destinationId: string, nowMs: number): SailResult;
  returnToNaturalCourse(nowMs: number): VoyageOrder;
  save(): SaveOutcome;
  saveProblem(): SaveProblem | null;
  saveFailure(): string | null;
  exportSave(): string;
  importSave(text: string): ImportResult;
  startNew(identityDraft?: FisherIdentity): StartResult;
  harbour(nowMs?: number): HarbourView;
  acceptCommission(id: string, nowMs: number): TransactionResult;
  claimCommission(id: string, nowMs: number): TransactionResult;
  cancelCommission(nowMs: number): TransactionResult;
  buyHarbourUpgrade(id: string, nowMs: number): TransactionResult;
  fundHarbourProject(id: string, nowMs: number): TransactionResult;
  completeHarbourChallenge(proof: HarbourGameProof, nowMs: number): TransactionResult;
  outfitter(): OutfitterView;
  buyCosmetic(id: string, nowMs: number): TransactionResult;
  equipCosmetic(slot: CosmeticSlot, id: string | null, nowMs: number): TransactionResult;
  updateIdentity(identity: FisherIdentity, nowMs: number): TransactionResult;
  recovery(): RecoveryStore;
  /** Constructs the rate-free catch DTO with an explicit lifecycle origin. */
  catchSnapshot(origin: CatchSnapshot['origin']): CatchSnapshot;
}
export interface OpenPlayerSessionOptions { readonly storage: SaveStorage; readonly nowMs: number; readonly seed: number; readonly key?: string; readonly now?: () => number; }

/**
 * ONE BACKUP PER SAVE KEY CHAIN. The shipped game saves on the sim's real chain and backs up
 * under `reelin.recovery.v1`; a session opened on any other key (the dev harness's
 * `reelin.world-dev.save.v1`) backs up beside that key. Before this, both chains shared the
 * one backup: a dev reset made the shipped title offer Save Recovery for a dev save, and a
 * dev import replaced the player's real backup.
 */
export function recoveryKeyFor(saveKey?: string): string {
  return saveKey === undefined || saveKey === SAVE_KEY ? 'reelin.recovery.v1' : `${saveKey}.recovery.v1`;
}
interface BackupRecord { readonly version: 1; readonly source: BackupSource; readonly timestamp: number; readonly text: string; }
function nowOf(options: OpenPlayerSessionOptions): number { return options.now?.() ?? options.nowMs; }
function validRecord(value: unknown): BackupRecord | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const r = value as Record<string, unknown>;
  return r['version'] === 1 && (r['source'] === 'import' || r['source'] === 'new-voyage' || r['source'] === 'manual') && typeof r['timestamp'] === 'number' && Number.isFinite(r['timestamp']) && typeof r['text'] === 'string' ? r as unknown as BackupRecord : null;
}

/** App-owned composition boundary. Neither sim nor voyage know profile exists. */
export function openPlayerSession(options: OpenPlayerSessionOptions): PlayerSession {
  const liveKey = options.key ?? SAVE_KEY;
  const RECOVERY_KEY = recoveryKeyFor(options.key);
  const RAW_SALVAGE_KEY = `${RECOVERY_KEY}.salvage`;
  // Inspect before opening: the simulation is allowed to offer a fresh in-memory fallback,
  // but the product must never let that fallback overwrite occupied invalid bytes.
  let problem: (SaveProblem & { readonly raw: string | null }) | null = null;
  for (const key of options.key === undefined ? SAVE_KEYS : [options.key]) {
    let raw: string | null;
    try { raw = options.storage.read(key); } catch { problem = { kind: 'unreadable', reason: 'storage-unreadable', rawAvailable: false, raw: null }; break; }
    if (raw === null) continue;
    const imported = importSave(raw, options.nowMs);
    if (!imported.ok) problem = { kind: 'invalid', reason: imported.reason, rawAvailable: true, raw };
    break;
  }
  let failedWrite: string | null = null;
  const trackedStorage: SaveStorage = {
    read: key => options.storage.read(key),
    write: (key, value) => {
      if (problem !== null && key === liveKey) { failedWrite = 'save-needs-recovery'; throw new Error(failedWrite); }
      try { options.storage.write(key, value); if (key === liveKey) failedWrite = null; }
      catch (error) { if (key === liveKey) failedWrite = 'storage-unwritable'; throw error; }
    },
    remove: key => options.storage.remove(key),
  };
  interface Cell { storage: SaveStorage; profile: PlayerProfile | null; }
  let active: Cell = { storage: trackedStorage, profile: null };
  const openIn = (cell: Cell, nowMs: number, seed: number): VoyageSession => openVoyageSession({
    storage: profileStorage({ read: key => cell.storage.read(key), write: (key, value) => cell.storage.write(key, value), remove: key => cell.storage.remove(key) }, () => cell.profile),
    nowMs,
    seed,
    ...(options.key === undefined ? {} : { key: options.key }),
  });
  let voyage = openIn(active, options.nowMs, options.seed);
  active.profile = profileFromEnvelope(voyage.envelope(), options.nowMs);

  let lastMooredAt: string | null = null;
  let lastObservedAt = Number.NEGATIVE_INFINITY;
  function reconcile(nowMs = nowOf(options), view: VoyageView = voyage.view(nowMs)): boolean {
    let arrivedNow = false;
    active.profile = reconcileProgress(profile(), voyage.state(), view);
    const moored = view.fix.phase === 'moored' ? view.fix.mooredAt : null;
    // A historical read is useful for a pure view, but cannot become a new live
    // arrival edge after a later tick has already been observed.
    const chronological = nowMs >= lastObservedAt;
    if (chronological && moored !== null && moored !== lastMooredAt && (moored === 'murrin-nearshore' || moored === 'murrin-outer-shelf')) {
      const current = profile();
      const result = destinationResult(moored, voyage.state());
      active.profile = {
        ...current,
        arrivedDestinations: current.arrivedDestinations.includes(moored) ? current.arrivedDestinations : [...current.arrivedDestinations, moored],
        arrivalResults: result === null || current.arrivalResults[moored] !== undefined ? current.arrivalResults : { ...current.arrivalResults, [moored]: result.id },
        arrivalTargets: current.arrivalTargets[moored] !== undefined ? current.arrivalTargets : { ...current.arrivalTargets, [moored]: voyage.state().targetSpecies || 'natural-course' },
      };
      arrivedNow = !current.arrivedDestinations.includes(moored);
      active.profile = reconcileProgress(profile(), voyage.state(), view);
    }
    if (chronological) {
      const priorHarbour = profile().harbour; const nextHarbour = observeHarbour(priorHarbour, view.fix.mooredAt);
      if (nextHarbour !== priorHarbour) { active.profile = { ...profile(), harbour: nextHarbour }; arrivedNow = true; }
    }
    if (chronological) { lastMooredAt = moored; lastObservedAt = nowMs; }
    return arrivedNow;
  }
  function profile(): PlayerProfile { return active.profile ?? createPlayerProfile(options.nowMs); }
  function exportCurrent(): string { if (problem?.raw !== null && problem?.raw !== undefined) return problem.raw; reconcile(); return withPlayerProfile(withVoyageOrder(voyage.exportText(), voyage.order()), profile()); }
  function candidate(text: string, nowMs: number, seed: number): { voyage: VoyageSession; cell: Cell } {
    const bytes = new Map<string, string>([[liveKey, text]]);
    const cell: Cell = { storage: { read: key => bytes.get(key) ?? null, write: (key, value) => bytes.set(key, value), remove: key => bytes.delete(key) }, profile: null };
    const candidateVoyage = openIn(cell, nowMs, seed);
    cell.profile = profileFromEnvelope(candidateVoyage.envelope(), nowMs);
    return { voyage: candidateVoyage, cell };
  }
  function replace(next: { voyage: VoyageSession; cell: Cell }): SaveOutcome {
    const text = withPlayerProfile(withVoyageOrder(next.voyage.exportText(), next.voyage.order()), next.cell.profile ?? createPlayerProfile(options.nowMs));
    // Keep corrupt bytes independently of the rolling playable backup, including when
    // Restore brings back a valid backup. A later normal save never touches this slot.
    if (problem?.raw !== null && problem?.raw !== undefined) {
      try { options.storage.write(RAW_SALVAGE_KEY, problem.raw); } catch { failedWrite = 'recovery-unwritable'; return { written: false, note: 'recovery-unwritable' }; }
    }
    try { options.storage.write(liveKey, text); } catch { failedWrite = 'storage-unwritable'; return { written: false, note: 'storage-unwritable' }; }
    problem = null; failedWrite = null;
    next.cell.storage = trackedStorage;
    voyage = next.voyage; active = next.cell;
    lastMooredAt = null; lastObservedAt = Number.NEGATIVE_INFINITY;
    return { written: true, note: '' };
  }
  const recoveryStore: RecoveryStore = {
    inspect(): RecoverySummary {
      let text: string | null;
      try { text = options.storage.read(RECOVERY_KEY); } catch { return { available: false, source: null, timestamp: null, note: 'recovery-unreadable' }; }
      let salvage = false; try { salvage = options.storage.read(RAW_SALVAGE_KEY) !== null; } catch { /* playable backup can still be inspected */ }
      const extra = salvage ? { salvageAvailable: true } : {};
      if (text === null) return { available: false, source: null, timestamp: null, note: '', ...extra };
      try {
        const record = validRecord(JSON.parse(text));
        if (record === null) return { available: false, source: null, timestamp: null, note: 'recovery-corrupt', salvageAvailable: true };
        const valid = importSave(record.text, nowOf(options)).ok;
        return valid ? { available: true, source: record.source, timestamp: record.timestamp, note: '', ...extra } : { available: false, source: record.source, timestamp: record.timestamp, note: 'backup-save-invalid', salvageAvailable: true };
      } catch { return { available: false, source: null, timestamp: null, note: 'recovery-corrupt', salvageAvailable: true }; }
    },
    backup(source: BackupSource, nowMs: number): SaveOutcome {
      let text: string | null;
      try { text = problem?.raw ?? options.storage.read(liveKey); } catch { return { written: false, note: 'recovery-unreadable' }; }
      try { options.storage.write(RECOVERY_KEY, JSON.stringify({ version: 1, source, timestamp: nowMs, text: text ?? exportCurrent() } satisfies BackupRecord)); return { written: true, note: '' }; } catch { return { written: false, note: 'recovery-unwritable' }; }
    },
    restore(nowMs: number): ImportResult {
      let raw: string | null;
      try { raw = options.storage.read(RECOVERY_KEY); } catch { return { ok: false, reason: 'recovery-unreadable' }; }
      if (raw === null) return { ok: false, reason: 'recovery-unavailable' };
      let record: BackupRecord | null = null; try { record = validRecord(JSON.parse(raw)); } catch { /* handled below */ }
      if (record === null) return { ok: false, reason: 'recovery-corrupt' };
      const validated = importSave(record.text, nowMs); if (!validated.ok) return validated;
      const next = candidate(record.text, nowMs, options.seed); const written = replace(next);
      return written.written ? { ok: true, save: next.voyage.envelope(), notes: [] } : { ok: false, reason: written.note };
    },
    clearBackup(): SaveOutcome { try { options.storage.remove(RECOVERY_KEY); return { written: true, note: '' }; } catch { return { written: false, note: 'recovery-unremovable' }; } },
    exportRaw(): string | null { try { const salvage = options.storage.read(RAW_SALVAGE_KEY); if (salvage !== null) return salvage; const raw = options.storage.read(RECOVERY_KEY); if (raw === null) return null; try { return validRecord(JSON.parse(raw))?.text ?? raw; } catch { return raw; } } catch { return null; } },
  };
  function transactional(text: string, source: BackupSource, nowMs: number): ImportResult {
    const validated = importSave(text, nowMs); if (!validated.ok) return validated;
    const backed = recoveryStore.backup(source, nowMs); if (!backed.written) return { ok: false, reason: backed.note };
    const next = candidate(text, nowMs, options.seed); const written = replace(next);
    return written.written ? { ok: true, save: next.voyage.envelope(), notes: validated.notes } : { ok: false, reason: written.note };
  }
  function transactProfile(nowMs: number, update: (current: PlayerProfile, earnedCoins: number) => { readonly ok: true; readonly value: PlayerProfile } | { readonly ok: false; readonly reason: string }): TransactionResult {
    if (problem !== null) return { ok: false, reason: 'save-needs-recovery' };
    // Natural earnings settle under the old profile. Purchase/identity changes are staged
    // separately and become visible only after the complete live-slot write succeeds.
    const settled = voyage.tick(nowMs); reconcile(nowMs, settled);
    const changed = update(profile(), voyage.state().coins);
    if (!changed.ok) return changed;
    const nextProfile = normalisePlayerProfile({ ...changed.value, lastSavedAtMs: nowMs }, nowMs);
    const text = withPlayerProfile(withVoyageOrder(voyage.exportText(), voyage.order()), nextProfile);
    const next = candidate(text, nowMs, options.seed);
    const written = replace(next);
    return written.written ? { ok: true } : { ok: false, reason: written.note };
  }
  function harbourContext(nowMs: number, view = voyage.view(nowMs)): HarbourContext {
    const state = voyage.state();
    return { portId: harbourIdentity(view.fix.mooredAt)?.id ?? null, catches: state.catches, unlockedSpecies: unlockedSpecies(levelForXp(state.xp)).map(s => s.id), catchCap: MAX_COUNT, simCoins: state.coins, outfitterSpent: profile().outfitter.spentCoins, discovered: view.chart.discovered.map(p => p.id), nowMs };
  }
  function transactHarbour(nowMs: number, update: (state: HarbourState, context: HarbourContext) => HarbourChange): TransactionResult {
    return transactProfile(nowMs, current => { const change = update(current.harbour, harbourContext(nowMs)); return change.ok ? { ok: true, value: { ...current, harbour: change.value } } : change; });
  }
  function currentOutfitter(): OutfitterView {
    const current = profile(); const simCoins = voyage.state().coins; const balance = harbourWallet(simCoins, current.harbour, current.outfitter.spentCoins);
    const view = outfitterView(harbourOutfitterFunds(simCoins, current.harbour, current.outfitter.spentCoins), current.outfitter);
    return { ...view, earnedCoins: Math.min(Number.MAX_SAFE_INTEGER, simCoins + current.harbour.bonusCoins), spentCoins: Math.min(Number.MAX_SAFE_INTEGER, current.outfitter.spentCoins + current.harbour.spentCoins), balance, items: view.items.map(item => ({ ...item, affordable: balance >= item.cost })) };
  }
  return {
    voyage: () => voyage,
    profile: () => { reconcile(); return profile(); },
    updateProfile(update): void { active.profile = normalisePlayerProfile(update(profile()), nowOf(options)); },
    updateSettings(patch): void { active.profile = normalisePlayerProfile({ ...profile(), settings: { ...profile().settings, ...patch } }, nowOf(options)); },
    setTutorial(mode): void { active.profile = normalisePlayerProfile({ ...profile(), tutorial: mode }, nowOf(options)); },
    tick(nowMs): VoyageView { const view = voyage.tick(nowMs); if (reconcile(nowMs, view)) voyage.save(nowMs); return view; },
    setTarget(speciesId, nowMs): void { const settled = voyage.tick(nowMs); reconcile(nowMs, settled); voyage.setTarget(speciesId, nowMs); },
    sailTo(destinationId, nowMs): SailResult {
      const before = profile();
      if (destinationId === 'murrin-nearshore' || destinationId === 'murrin-outer-shelf') active.profile = { ...before, chosenDestinations: before.chosenDestinations.includes(destinationId) ? before.chosenDestinations : [...before.chosenDestinations, destinationId] };
      const result = voyage.sailTo(destinationId, nowMs);
      if (!result.ok) active.profile = before;
      return result;
    },
    returnToNaturalCourse(nowMs): VoyageOrder { const settled = voyage.tick(nowMs); reconcile(nowMs, settled); return voyage.returnToTrack(nowMs); },
    save(): SaveOutcome { reconcile(); const before = profile(); active.profile = { ...before, lastSavedAtMs: nowOf(options) }; const result = voyage.save(nowOf(options)); if (!result.written) active.profile = before; return result; },
    saveProblem: () => problem === null ? null : { kind: problem.kind, reason: problem.reason, rawAvailable: problem.rawAvailable },
    saveFailure: () => failedWrite,
    exportSave: exportCurrent,
    importSave: text => transactional(text, 'import', nowOf(options)),
    startNew(identityDraft): StartResult {
      if (identityDraft !== undefined) { const valid = validateFisherIdentity(identityDraft); if (!valid.ok) return valid; }
      const nowMs = nowOf(options); const fresh = createSave(options.seed, nowMs);
      const preferences = profile(); const base = profileFromEnvelope(fresh, nowMs);
      const freshProfile = { ...base, settings: preferences.settings, skippedTutorialSteps: preferences.skippedTutorialSteps,
        tutorial: preferences.tutorial.kind === 'skipped' ? preferences.tutorial : base.tutorial,
        ...(identityDraft === undefined ? {} : { identity: normaliseFisherIdentity(identityDraft) }) };
      const nextText = withPlayerProfile(exportSave(fresh), freshProfile);
      let occupied: boolean;
      try { occupied = options.storage.read(liveKey) !== null; } catch { return { ok: false, reason: 'recovery-unreadable' }; }
      if (occupied) { const backed = recoveryStore.backup('new-voyage', nowMs); if (!backed.written) return { ok: false, reason: backed.note }; }
      const next = candidate(nextText, nowMs, options.seed); const written = replace(next);
      return written.written ? { ok: true } : { ok: false, reason: written.note };
    },
    harbour(nowMs = nowOf(options)): HarbourView { reconcile(nowMs); return harbourView(profile().harbour, harbourContext(nowMs)); },
    acceptCommission: (id, nowMs) => transactHarbour(nowMs, (state, ctx) => acceptHarbourCommission(state, ctx, id)),
    claimCommission: (id, nowMs) => transactHarbour(nowMs, (state, ctx) => claimHarbourCommission(state, ctx, id)),
    cancelCommission: nowMs => transactHarbour(nowMs, state => ({ ok: true, value: state.activeCommission === null ? state : { ...state, activeCommission: null } })),
    buyHarbourUpgrade: (id, nowMs) => transactHarbour(nowMs, (state, ctx) => buyHarbourEquipment(state, ctx, id)),
    fundHarbourProject: (id, nowMs) => transactHarbour(nowMs, (state, ctx) => fundHarbourProject(state, ctx, id)),
    completeHarbourChallenge(proof, nowMs): TransactionResult {
      const valid = validateHarbourGameProof(proof); if (!valid.ok) return valid;
      return transactHarbour(nowMs, (state, ctx) => rewardHarbourChallenge(state, ctx, { ...valid.proof, clearKey: valid.clearKey }));
    },
    outfitter: currentOutfitter,
    buyCosmetic(id, nowMs): TransactionResult {
      return transactProfile(nowMs, (current, earnedCoins) => {
        const changed = buyOutfitterItem(harbourOutfitterFunds(earnedCoins, current.harbour, current.outfitter.spentCoins), current.outfitter, id);
        return changed.ok ? { ok: true, value: { ...current, outfitter: changed.value } } : changed;
      });
    },
    equipCosmetic(slot, id, nowMs): TransactionResult {
      return transactProfile(nowMs, current => {
        const changed = equipOutfitterItem(current.outfitter, slot, id);
        return changed.ok ? { ok: true, value: { ...current, outfitter: changed.value } } : changed;
      });
    },
    updateIdentity(identity, nowMs): TransactionResult {
      const valid = validateFisherIdentity(identity); if (!valid.ok) return valid;
      return transactProfile(nowMs, current => ({ ok: true, value: { ...current, identity: { ...current.identity, ...normaliseFisherIdentity(identity) } } }));
    },
    recovery: () => recoveryStore,
    catchSnapshot(origin): CatchSnapshot { const state = voyage.state(); return { origin, catches: state.catches, records: state.records }; },
  };
}
